import { useCallback, useState } from 'react';
import { fetchCustomers } from '@/lib/api/customers';
import { getLocalTimezone, stanNotify } from '@/lib';
import { downloadCSV, generateCSV, generateExportFilename } from '../utils/csv.utils';
import type { Customer } from '../types/customers.types';

const EXPORT_PAGE_SIZE = 1000;
const CONCURRENT_REQUESTS = 5;

interface UseExportCsvProps {
    filterArray: string | undefined;
    totalPages: number;
    onSuccess?: () => void;
}

interface ExportState {
    isExporting: boolean;
    currentPage: number;
    totalExportPages: number;
    progress: number;
}

export function useExportCsv({ filterArray, totalPages, onSuccess }: UseExportCsvProps) {
    const totalExportPages = Math.ceil((totalPages * 10) / EXPORT_PAGE_SIZE) || 1;

    const [state, setState] = useState<ExportState>({
        isExporting: false,
        currentPage: 0,
        totalExportPages,
        progress: 0,
    });

    const resetState = useCallback(() => {
        setState({
            isExporting: false,
            currentPage: 0,
            totalExportPages,
            progress: 0,
        });
    }, [totalExportPages]);

    const exportCsv = useCallback(async () => {
        setState(prev => ({
            ...prev,
            isExporting: true,
            currentPage: 0,
        }));

        const allData: Customer[] = [];
        const allFailedPages: number[] = [];

        try {
            // Fetch pages in concurrent batches
            for (let batchStart = 1; batchStart <= totalExportPages; batchStart += CONCURRENT_REQUESTS) {
                const batchEnd = Math.min(batchStart + CONCURRENT_REQUESTS - 1, totalExportPages);
                const pagesToFetch = Array.from({ length: batchEnd - batchStart + 1 }, (_, i) => batchStart + i);

                setState(prev => ({
                    ...prev,
                    currentPage: batchEnd,
                    progress: (batchEnd / totalExportPages) * 100,
                }));

                const batchResponses = await Promise.allSettled(
                    pagesToFetch.map(page =>
                        fetchCustomers({
                            page,
                            pageSize: EXPORT_PAGE_SIZE,
                            filter_array: filterArray,
                            timezone: getLocalTimezone(),
                        })
                    )
                );

                // Process successful responses and track failures
                const failedPages: number[] = [];
                batchResponses.forEach((result, index) => {
                    if (result.status === 'fulfilled') {
                        allData.push(...result.value.data);
                    } else {
                        const pageNumber = pagesToFetch[index];
                        if (pageNumber !== undefined) {
                            failedPages.push(pageNumber);
                        }
                    }
                });

                // Track all failed pages across batches
                allFailedPages.push(...failedPages);
            }

            // If we didn't get any data at all, throw error
            if (allData.length === 0) {
                throw new Error(
                    allFailedPages.length > 0
                        ? `Failed to fetch customer data from ${allFailedPages.length} page(s)`
                        : 'No customer data available to export'
                );
            }

            // Generate and download CSV
            const csvContent = generateCSV(allData);
            const filename = generateExportFilename();
            downloadCSV(csvContent, filename);

            // Show warning if some pages failed, otherwise success
            if (allFailedPages.length > 0) {
                stanNotify({
                    type: 'warning',
                    title: 'Export Partially Complete',
                    text: `Exported ${allData.length} customers, but ${allFailedPages.length} page(s) failed to load.`,
                    duration: 5000,
                });
            } else {
                stanNotify({
                    type: 'success',
                    title: 'Export Complete',
                    text: `Successfully exported ${allData.length} customers.`,
                    duration: 5000,
                });
            }

            resetState();
            onSuccess?.();
        } catch (error) {
            console.error('Export failed:', error);
            stanNotify({
                type: 'error',
                title: 'Export Failed',
                text: 'Failed to export customers. Please try again.',
                duration: 5000,
            });
            resetState();
        }
    }, [totalExportPages, filterArray, onSuccess, resetState]);

    return {
        ...state,
        exportCsv,
        resetState,
    };
}
